import { useEffect, useState } from 'react'
import { usePostHog } from '@posthog/react'
import { useLocation } from 'react-router-dom'
import { get as getWebAuthnCredential } from '@github/webauthn-json'
import Loader from '../components/Loading'
import {
  appleLogin,
  beginPasskeyLogin,
  confirmPasswordReset,
  finishPasskeyLogin,
  requestMagicLink,
  requestPasswordReset,
  scribblLogin,
} from '../api/user'
import { createOAuthState, setOAuthProvider } from '../utils/sessionAuth'
import { getAppRedirectUri } from '../utils/appOrigin'
import { loadFeatureFlagOverrides, saveFeatureFlagOverrides } from '../utils/featureFlags'
import SignUpFormView from './SignUpFormView'
import '../style/views/LoginView.css'
import '../style/components/PasswordRequirements.css'

type AuthStep = 'initial' | 'code_required' | 'loading' | 'error'

type ViewMode = 'login' | 'signup' | 'forgot-password'
type VerificationMethod = 'email' | 'cell'
type ForgotPasswordStep = 'request' | 'confirm'


const GOOGLE_OAUTH_SCOPES = [
  'openid',
  'email',
  'profile',
].join(' ')

const USE_AZURE_API =
  (import.meta.env.VITE_USE_AZURE_API as string | undefined)?.toLowerCase() !== 'false'
const AZURE_OAUTH_SCOPES = [
  'openid',
  'profile',
  'email',
  'User.Read',
  'offline_access',
  ...(USE_AZURE_API ? ['Calendars.ReadWrite'] : []),
].join(' ')

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
    AppleID: any
  }
}

interface LoginViewProps {
  isChecking?: boolean
  onAuthResolved?: (session: {
    email: string | null
    exp?: number
    token: string
    userId: string | null
    firstName?: string
    lastName?: string
    provider?: 'google' | 'azure' | 'apple' | null
  }) => void
}

export function buildGoogleOAuthUrl({
  clientId,
  redirectUri,
  state,
}: {
  clientId: string
  redirectUri: string
  state: string | null
}) {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: GOOGLE_OAUTH_SCOPES,
  })
  if (state) {
    params.set('state', state)
    params.set('nonce', state)
  }

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
}

export default function LoginView({ isChecking = false, onAuthResolved }: LoginViewProps) {
  const posthog = usePostHog()
  const location = useLocation()
  const [isDark, setIsDark] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authStep, setAuthStep] = useState<AuthStep>('initial')
  const [error, setError] = useState<string | null>(null)
  const [loginNotice, setLoginNotice] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('login')
  const [resetEmail, setResetEmail] = useState('')
  const [resetVerify, setResetVerify] = useState<VerificationMethod | null>(null)
  const [resetStep, setResetStep] = useState<ForgotPasswordStep>('request')
  const [resetCode, setResetCode] = useState('')
  const [resetPassword, setResetPassword] = useState('')
  const [resetConfirmPassword, setResetConfirmPassword] = useState('')
  const [resetStatus, setResetStatus] = useState<string | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)
  const [isResetLoading, setIsResetLoading] = useState(false)
  const [resetPasswordFocused, setResetPasswordFocused] = useState(false)
  const [resetConfirmPasswordFocused, setResetConfirmPasswordFocused] = useState(false)

  const passwordRequirements = {
    hasUpper: /[A-Z]/.test(resetPassword),
    hasLower: /[a-z]/.test(resetPassword),
    hasNumber: /[0-9]/.test(resetPassword),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(resetPassword),
    isMinLength: resetPassword.length >= 8,
  }

  useEffect(() => {
    const value = new URLSearchParams(location.search).get('newauth')
    if (value !== '1' && value !== '0') return

    saveFeatureFlagOverrides({
      ...loadFeatureFlagOverrides(),
      'new-auth': value === '1',
    })
  }, [location.search])

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean)
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- TODO(ENG-399): investigate if this expression is intentional
  authStep;

  useEffect(() => {
    const syncTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme')
      setIsDark(theme === 'dark')
    }
    syncTheme()
    const observer = new MutationObserver(syncTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (window.AppleID) {
      const clientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined
      if (clientId) {
        window.AppleID.auth.init({
          clientId,
          scope: 'name email',
          redirectURI: getAppRedirectUri(),
          usePopup: true,
        })
      }
    }
  }, [])

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoginNotice(null)
    setIsLoading(true)
    setAuthStep('loading')

    try {
      const tokenJson = await scribblLogin({ username: email, password })
      const accessToken = (tokenJson.access_token || tokenJson.token) as string | undefined
      if (!accessToken) {
        throw new Error('Missing access token')
      }

      if (onAuthResolved) {
        posthog?.capture('user_logged_in', { auth_provider: 'email' })
        onAuthResolved({
          email: (tokenJson.email as string) || email,
          token: accessToken,
          userId: (tokenJson.user_id || tokenJson.userId) as string | null,
          firstName: tokenJson.first_name as string | undefined,
          lastName: tokenJson.last_name as string | undefined,
          provider: 'azure',
        })
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
    } catch (err: any) {
      posthog?.captureException(err, { context: 'email_login' })
      console.error('Email login error:', err)
      setAuthStep('error')
      setError(err.message || 'Failed to sign in. Please check your credentials.')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasskeyLogin = async () => {
    setError(null)
    setLoginNotice(null)
    setIsLoading(true)
    setAuthStep('loading')
    try {
      const options = await beginPasskeyLogin(email || undefined)
      const credential = await getWebAuthnCredential(options as Parameters<typeof getWebAuthnCredential>[0])
      const tokenJson = await finishPasskeyLogin(credential)
      const accessToken = (tokenJson.access_token || tokenJson.token) as string | undefined
      if (!accessToken) throw new Error('Missing access token')
      posthog?.capture('user_logged_in', { auth_provider: 'passkey' })
      onAuthResolved?.({
        email: (tokenJson.email as string) || email || null,
        token: accessToken,
        userId: (tokenJson.user_id || tokenJson.userId || tokenJson.sub) as string | null,
        firstName: tokenJson.first_name as string | undefined,
        lastName: tokenJson.last_name as string | undefined,
        provider: null,
      })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WebAuthn throws DOMException-like values.
    } catch (err: any) {
      posthog?.captureException(err, { context: 'passkey_login' })
      setAuthStep('error')
      setError(err.message || 'Passkey sign-in failed.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMagicLink = async () => {
    setError(null)
    setLoginNotice(null)
    if (!email) {
      setError('Enter your email to receive a magic link.')
      return
    }
    setIsLoading(true)
    try {
      await requestMagicLink(email)
      setLoginNotice('Check your email for a sign-in link.')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API errors are normalized by ApiError.
    } catch (err: any) {
      posthog?.captureException(err, { context: 'magic_link_request' })
      setError(err.message || 'Failed to send magic link.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
    if (!clientId) {
      console.error('Missing Google client id. Check your environment variables.')
      return
    }
    const state = createOAuthState('google')
    setOAuthProvider('google')
    const redirectUri = getAppRedirectUri()
    posthog?.capture('user_logged_in_with_google')
    window.location.href = buildGoogleOAuthUrl({ clientId, redirectUri, state })
  }

  const handleAzureLogin = () => {
    const clientId = import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined
    const tenantId = (import.meta.env.VITE_AZURE_TENANT_ID as string | undefined) || 'common'
    if (!clientId) {
      console.error('Missing Azure client id. Check your environment variables.')
      return
    }
    const state = createOAuthState('azure')
    setOAuthProvider('azure')
    const redirectUri = getAppRedirectUri()
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: AZURE_OAUTH_SCOPES,
      response_mode: 'query',
    })
    if (state) params.set('state', state)
    posthog?.capture('user_logged_in_with_azure')
    window.location.href = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`
  }

  const handleAppleLogin = async () => {
    if (!window.AppleID) {
      alert('Apple Sign In is not available. Please configure VITE_APPLE_CLIENT_ID.')
      return
    }
    try {
      const response = await window.AppleID.auth.signIn()
      const tokenJson = await appleLogin(response.authorization.id_token)

      if (onAuthResolved) {
        const email = tokenJson?.email as string | undefined
        const token = tokenJson?.token as string | undefined
        const userId = tokenJson?.userId ? String(tokenJson.userId) : null

        if (!token) {
          throw new Error('Server did not return a session token.')
        }

        posthog?.capture('user_logged_in_with_apple')
        onAuthResolved({
          email: email ?? null,
          token: token,
          userId,
          firstName: tokenJson.first_name as string | undefined,
          lastName: tokenJson.last_name as string | undefined,
          provider: 'apple',
        })
      }
    } catch (error) {
      posthog?.captureException(error, { context: 'apple_login' })
      console.error('Apple Login Error:', error)
      alert('Apple Sign In failed. Please try again.')
    }
  }

  const openForgotPassword = () => {
    setResetEmail(email)
    setResetVerify(null)
    setResetStep('request')
    setResetCode('')
    setResetPassword('')
    setResetConfirmPassword('')
    setResetStatus(null)
    setResetError(null)
    setError(null)
    setLoginNotice(null)
    setViewMode('forgot-password')
  }

  const handleForgotPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetError(null)
    setResetStatus(null)

    const trimmedEmail = resetEmail.trim()
    if (!trimmedEmail) {
      setResetError('Enter the email on your account.')
      return
    }
    if (!resetVerify) {
      setResetError('Choose whether to receive your code by email or phone.')
      return
    }

    setIsResetLoading(true)
    try {
      const body = await requestPasswordReset({ email: trimmedEmail, verify: resetVerify })
      const sentTo = body?.verify === 'cell' ? 'phone' : 'email'
      setResetVerify(body?.verify === 'cell' ? 'cell' : 'email')
      setResetStep('confirm')
      setResetCode('')
      setResetPassword('')
      setResetConfirmPassword('')
      setResetStatus(`We sent a reset code to your ${sentTo}.`)
    } catch (err) {
      posthog?.captureException(err, { context: 'forgot_password_request' })
      setResetError(err instanceof Error ? err.message : 'Unable to send a reset code.')
    } finally {
      setIsResetLoading(false)
    }
  }

  const handleForgotPasswordConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetError(null)
    setResetStatus(null)

    const trimmedEmail = resetEmail.trim()
    const trimmedCode = resetCode.trim()
    if (!trimmedEmail || !trimmedCode || !resetPassword) {
      setResetError('Enter your email, code, and new password.')
      return
    }
    if (!resetVerify) {
      setResetError('Choose whether you received your code by email or phone.')
      return
    }
    if (resetPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match.')
      return
    }
    if (!isPasswordValid) {
      setResetError('Password does not meet requirements.')
      return
    }

    setIsResetLoading(true)
    try {
      await confirmPasswordReset({
        email: trimmedEmail,
        verify: resetVerify,
        code: trimmedCode,
        password: resetPassword,
      })

      setPassword(resetPassword)
      setEmail(trimmedEmail)
      setViewMode('login')
      setAuthStep('initial')
      setError(null)
      setLoginNotice('Password reset. You can sign in now.')
      setResetStatus(null)
    } catch (err) {
      posthog?.captureException(err, { context: 'forgot_password_confirm' })
      setResetError(err instanceof Error ? err.message : 'Unable to reset your password.')
    } finally {
      setIsResetLoading(false)
    }
  }

  if (isChecking) {
    return (
      <div className="logged-out-shell">
        <Loader />
      </div>
    )
  }

  if (viewMode === 'signup') {
    return (
      <SignUpFormView
        onAuthResolved={onAuthResolved}
        onBackToLogin={() => setViewMode('login')}
      />
    )
  }

  if (viewMode === 'forgot-password') {
    return (
      <div className="logged-out-shell">
        <div className="card login-card">
          <div className="login-header">
            <div className="login-brand-pill">
              <img src="/logo.svg" alt="myScribbl Icon" className="login-pill-logo" />
              <img src={isDark ? "/wordmark-dm.png" : "/wordmark.png"} alt="myScribbl" className="login-wordmark" />
            </div>
            <h1>Reset password</h1>
            <p className="muted">
              {resetStep === 'request'
                ? 'Choose where to receive your verification code.'
                : 'Enter the code and choose a new password.'}
            </p>
          </div>

          {resetStep === 'request' ? (
            <form className="email-form" onSubmit={handleForgotPasswordRequest}>
              <div className="form-group">
                <label htmlFor="reset-email">Email</label>
                <input
                  id="reset-email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  placeholder="name@company.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoFocus
                  className="form-input"
                />
              </div>

              <fieldset className="verification-options">
                <legend>How should we send the code?</legend>
                <label>
                  <input
                    type="radio"
                    name="reset-verify"
                    value="email"
                    checked={resetVerify === 'email'}
                    onChange={() => setResetVerify('email')}
                  />
                  <span>Email</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="reset-verify"
                    value="cell"
                    checked={resetVerify === 'cell'}
                    onChange={() => setResetVerify('cell')}
                  />
                  <span>Phone</span>
                </label>
              </fieldset>

              {resetStatus && <div className="form-success">{resetStatus}</div>}
              {resetError && <div className="form-error">{resetError}</div>}

              <button type="submit" className="primary-button" disabled={isResetLoading}>
                {isResetLoading ? 'Sending code...' : 'Send code'}
              </button>
            </form>
          ) : (
            <form className="email-form" onSubmit={handleForgotPasswordConfirm}>
              <div className="reset-summary">
                Code sent by {resetVerify === 'cell' ? 'phone' : 'email'} for {resetEmail}
              </div>

              <div className="form-group">
                <label htmlFor="reset-code">Verification code</label>
                <input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  name="code"
                  autoComplete="one-time-code"
                  placeholder="1234"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  autoFocus
                  className="form-input"
                />
              </div>

              <div className="form-group password-label">
                <label htmlFor="reset-password">New password</label>
                <input
                  id="reset-password"
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  placeholder="New password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  onFocus={() => setResetPasswordFocused(true)}
                  onBlur={() => setResetPasswordFocused(false)}
                  required
                  className="form-input"
                />
                {resetPasswordFocused && (
                  <div className="password-requirements">
                    <p className={passwordRequirements.isMinLength ? 'met' : ''}>• At least 8 characters</p>
                    <p className={passwordRequirements.hasUpper ? 'met' : ''}>• At least one uppercase letter</p>
                    <p className={passwordRequirements.hasLower ? 'met' : ''}>• At least one lowercase letter</p>
                    <p className={passwordRequirements.hasNumber ? 'met' : ''}>• At least one number</p>
                    <p className={passwordRequirements.hasSpecial ? 'met' : ''}>• At least one special character</p>
                  </div>
                )}
              </div>

              <div className="form-group password-label">
                <label htmlFor="reset-confirm-password">Confirm password</label>
                <input
                  id="reset-confirm-password"
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  placeholder="Confirm password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  onFocus={() => setResetConfirmPasswordFocused(true)}
                  onBlur={() => setResetConfirmPasswordFocused(false)}
                  required
                  className="form-input"
                />
                {resetConfirmPasswordFocused && (
                  <div className="password-requirements">
                    <p className={resetPassword === resetConfirmPassword && resetConfirmPassword !== '' ? 'met' : ''}>
                      {resetPassword === resetConfirmPassword && resetConfirmPassword !== '' ? '• Passwords match' : '• Passwords do not match'}
                    </p>
                  </div>
                )}
              </div>

              {resetStatus && <div className="form-success">{resetStatus}</div>}
              {resetError && <div className="form-error">{resetError}</div>}

              <button type="submit" className="primary-button" disabled={isResetLoading}>
                {isResetLoading ? 'Resetting...' : 'Reset password'}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setResetStep('request')
                  setResetStatus(null)
                  setResetError(null)
                }}
              >
                Send a new code
              </button>
            </form>
          )}

          <div className="login-footer">
            <button type="button" className="link-button" onClick={() => setViewMode('login')}>
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="logged-out-shell">
      <div className="card login-card">
        <div className="login-header">
          <div className="login-brand-pill">
            <img src="/logo.svg" alt="myScribbl Icon" className="login-pill-logo" />
            <img src={isDark ? "/wordmark-dm.png" : "/wordmark.png"} alt="myScribbl" className="login-wordmark" />
          </div>
          <h1>Welcome back</h1>
        </div>
        
        <form className="email-form" onSubmit={handleEmailLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              autoComplete="username"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="form-input"
            />
          </div>
          
          {loginNotice && <div className="form-success">{loginNotice}</div>}
          {error && <div className="form-error">{error}</div>}
          
          <button type="submit" className="primary-button" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
          <button type="button" className="secondary-button" disabled={isLoading} onClick={handlePasskeyLogin}>
            Sign in with a passkey
          </button>
          <button type="button" className="link-button" disabled={isLoading} onClick={handleMagicLink}>
            Email me a magic link
          </button>
          <button type="button" className="link-button forgot-password-link" onClick={openForgotPassword}>
            Forgot password?
          </button>
        </form>

        <div className="social-divider">
          <span>or continue with</span>
        </div>

        <div className="login-actions">
          <button type="button" className="provider-button google-login" onClick={handleGoogleLogin}>
            <img src="/google_logo.png" alt="Google" className="brand-logo" />
          </button>
          <button type="button" className="provider-button azure-login" onClick={handleAzureLogin}>
            <img src="/ms.png" alt="Microsoft" className="brand-logo" />
          </button>
          {window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && (
            <button type="button" className="provider-button apple-login" onClick={handleAppleLogin}>
              <svg className="brand-logo" viewBox="0 0 384 512" width="20" height="20">
                <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 21.8-88.5 21.8-11.4 0-51.1-20.8-80.3-21.2-38.2-.4-81.7 22.1-105.5 64.1-41.3 72-12.7 175.7 32.7 241.6 22 31.9 48.6 65.1 82.3 64.3 31.1-.9 41.5-20.1 80.9-20.1 38.6 0 49.3 20.1 81.6 19.5 34.1-.6 57.1-30 79-62.1 25-36.3 35.3-71.3 35.6-73.2-.8-.4-68.5-26.2-68.7-105.3zm-77.9-188.8c15.1-18.2 25.1-43.4 22.4-68.8-22.3 1-49.3 14.8-65.1 33.3-14.1 16.4-26.5 42.1-23.2 67.2 24.9 1.9 49.8-13.4 65.9-31.7z"/>
              </svg>
            </button>
          )}
        </div>

        <div className="login-footer">
          <p className="muted">
            Don't have an account?{' '}
            <button type="button" className="link-button" onClick={() => setViewMode('signup')}>
              Create one
            </button>
          </p>
          <p className="muted">
            By signing in, you agree to our{' '}
            <a
              href="https://www.myscribbl.com/terms-conditions"
              target="_blank"
              rel="noreferrer"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href="https://www.myscribbl.com/privacy-policy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>.
          </p>
        </div>
      </div>
    </div>
  )
}
